// src/debug.tsx
import React from "react";
function Debug(props) {
  const { debug, ...rest } = props;
  return /* @__PURE__ */ React.createElement("details", { ...rest }, /* @__PURE__ */ React.createElement("summary", { onClick: () => console.log("[Debug]", debug) }, "debug"), /* @__PURE__ */ React.createElement("pre", null, JSON.stringify(debug, null, 2)));
}

// src/compose.tsx
import React2 from "react";
function Compose({ elements }) {
  return elements.reduceRight(
    (acc, el) => React2.cloneElement(el, {}, acc),
    /* @__PURE__ */ React2.createElement(React2.Fragment, null)
  );
}

// src/utils.ts
import React3 from "react";
function usePrevious(value) {
  const ref = React3.useRef(value);
  React3.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}
function useStableRef(value) {
  const ref = React3.useRef(value);
  const useEffect = typeof window === "undefined" ? React3.useEffect : React3.useLayoutEffect;
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}
function useStableCallback(callback) {
  const ref = useStableRef(callback);
  const wrapper = (...args) => ref.current(...args);
  return React3.useCallback(wrapper, []);
}
function useRerender() {
  return React3.useReducer((prev) => !prev, false)[1];
}
function useRefCallbackEffect(effect) {
  const stableEffect = useStableCallback(effect);
  const destructorRef = React3.useRef();
  const refCallback = (value) => {
    if (value) {
      destructorRef.current = stableEffect(value);
    } else {
      destructorRef.current?.();
    }
  };
  return React3.useCallback(refCallback, []);
}

// src/request-animation-frame.ts
import React4 from "react";
function useRafLoop(callback) {
  const stableCallback = useStableCallback(callback);
  React4.useEffect(() => {
    let id;
    function loop(time) {
      id = requestAnimationFrame(loop);
      stableCallback(time);
    }
    loop();
    return () => {
      if (typeof id === "number") {
        cancelAnimationFrame(id);
      }
    };
  }, []);
}
function useRafTime(enabled) {
  const [time, setTime] = React4.useState();
  const [origin, setOrigin] = React4.useState();
  React4.useEffect(() => {
    setTime(void 0);
    setOrigin(void 0);
  }, [enabled]);
  useRafLoop((next) => {
    if (!enabled) {
      return;
    }
    if (typeof next === "undefined" || typeof origin === "undefined") {
      setOrigin(next);
    } else {
      setTime((time2) => (time2 ?? 0) + (next - origin));
    }
  });
  return time;
}

// src/set-state.ts
import React5 from "react";
function toDelayedSetState(setState) {
  const [subscribe, setSubscribe] = React5.useState();
  function setStateDelayed(value, ms) {
    setSubscribe(() => () => {
      const unsubscribe = setTimeout(() => {
        setState(value);
        setSubscribe(void 0);
      }, ms);
      return () => {
        clearTimeout(unsubscribe);
      };
    });
  }
  function reset() {
    setSubscribe(void 0);
  }
  const pending = Boolean(subscribe);
  React5.useEffect(() => subscribe?.(), [subscribe]);
  return [
    React5.useCallback(setStateDelayed, [setState]),
    React5.useCallback(reset, []),
    pending
  ];
}
function toImmutableSetState(setState, mutableMethod, shallowCopy) {
  const wrapper = (...args) => {
    setState((prev) => {
      prev = shallowCopy(prev);
      mutableMethod.bind(prev)(...args);
      return prev;
    });
  };
  return wrapper;
}
function toArraySetState(setState) {
  const pt = Array.prototype;
  const copy = (ls) => [...ls];
  return {
    push: toImmutableSetState(setState, pt.push, copy),
    sort: toImmutableSetState(setState, pt.sort, copy),
    splice: toImmutableSetState(setState, pt.splice, copy),
    toggle: toImmutableSetState(
      setState,
      toggleArray,
      copy
    )
  };
}
function toSetSetState(setState) {
  const pt = Set.prototype;
  const copy = (ls) => new Set(ls);
  return {
    add: toImmutableSetState(setState, pt.add, copy),
    delete: toImmutableSetState(setState, pt.delete, copy),
    clear: toImmutableSetState(setState, pt.clear, copy),
    toggle: toImmutableSetState(
      setState,
      toggleSet,
      copy
    )
  };
}
function toggleArray(value) {
  const index = this.indexOf(value);
  if (index < 0) {
    this.push(value);
  } else {
    this.splice(index, 1);
  }
}
function toggleSet(value) {
  if (this.has(value)) {
    this.delete(value);
  } else {
    this.add(value);
  }
}

// src/match-media.tsx
import React6 from "react";
function useMatchMedia(query) {
  const result = React6.useMemo(() => window.matchMedia(query), [query]);
  const rerender = useRerender();
  React6.useEffect(() => {
    result.addEventListener("change", rerender);
    return () => result.removeEventListener("change", rerender);
  }, [result]);
  return result;
}
export {
  Compose,
  Debug,
  toArraySetState,
  toDelayedSetState,
  toImmutableSetState,
  toSetSetState,
  useMatchMedia,
  usePrevious,
  useRafLoop,
  useRafTime,
  useRefCallbackEffect,
  useRerender,
  useStableCallback,
  useStableRef
};
